import React, { useRef, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { styled } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/standard-button';
import { token } from '@atlaskit/tokens';

import { expValEquals } from '@confluence/feature-experiments';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';

import type { RepositionImageComponentProps } from '../pageCoverPictureTypes.ts';
import { DEFAULT_POSITION_VALUE } from '../pageCoverPictureConstants.ts';

const i18n = defineMessages({
	dragToReposition: {
		id: 'page-cover-picture.drag.to.reposition.message',
		description:
			'Small overlay message that appears on top of cover picture when image repositioning is active.',
		defaultMessage: 'Drag to reposition',
	},
	saveReposition: {
		id: 'page-cover-picture.save.reposition',
		description:
			"Message on button which user can click to save their image repositioning when they're done.",
		defaultMessage: 'Apply',
	},
	cancelReposition: {
		id: 'page-cover-picture.cancel.reposition',
		description: 'Message on button which user can click to cancel image repositioning.',
		defaultMessage: 'Cancel',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RepositionWrapper = styled.div<{
	userOffline?: boolean;
}>({
	display: 'flex',
	alignItems: 'center',
	background: token('color.background.neutral'),
	visibility: 'visible',
	height: '30px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	cursor: ({ userOffline }) => (userOffline ? 'not-allowed' : 'default'),
	width: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DragToRepositionTextWrapper = styled.span({
	font: token('font.body.large'),
	color: token('color.background.input'),
	display: 'flex',
	width: '120%',
	justifyContent: 'flex-end',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RepositionButtonText = styled.span({
	font: token('font.body'),
	color: token('color.text'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RepositionButtonWrapper = styled.span({
	width: '95%',
	display: 'flex',
	justifyContent: 'flex-end',
	marginRight: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RepositionImageWrapper = styled.span<{
	userOffline?: boolean;
	dynamicCoverImageHeight?: number;
}>({
	position: 'relative',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	cursor: ({ userOffline }) => (userOffline ? 'not-allowed' : 'ns-resize'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: ({ dynamicCoverImageHeight }) => `${dynamicCoverImageHeight}px`,
	width: '100%',
	display: 'flex',
	alignItems: 'flex-start',
});

export const ImageRepositioner = ({
	initialPicturePosition,
	onSaveReposition,
	setIsRepositionActive,
	setShouldShowReposition,
	coverPictureState,
	forceUpdate,
	userOffline,
	dynamicCoverImageHeight,
	analyticsSource = 'editorPageCoverPicture',
}: RepositionImageComponentProps): React.JSX.Element => {
	const userOfflineRef = useRef(userOffline);

	useEffect(() => {
		userOfflineRef.current = userOffline;
	}, [userOffline]);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const isLivePage = useIsCurrentPageLive();

	const handleRepositionImageMouseDown = () => {
		// if we are here that means reposition is active since the div is visible to the user and user just moused down inside the image
		// they want to reposition
		document.addEventListener('mousemove', handleMouseMove);
		document.addEventListener('mouseup', handleMouseUp);
	};

	const handleMouseUp = () => {
		removeListeners();
	};

	let oldClientY = 0;
	const curPos = useRef<number>(initialPicturePosition || DEFAULT_POSITION_VALUE);
	const handleMouseMove = (e) => {
		if (userOfflineRef.current) {
			removeListeners();
			return;
		}
		e.preventDefault(); // prevent text selection
		const img = document.getElementById('actual-cover-picture');
		if (!img) {
			return;
		}
		if (e.clientY > oldClientY) {
			// mouse moved down
			if (curPos.current - 0.75 >= 0) {
				curPos.current -= 0.75;
				img.style.objectPosition = `center ${curPos.current}%`;
			}
		} else if (e.clientY < oldClientY) {
			// mouse moved up
			if (curPos.current + 0.75 <= 100) {
				curPos.current += 0.75;
				img.style.objectPosition = `center ${curPos.current}%`;
			}
		}
		oldClientY = e.clientY;
	};

	const removeListeners = () => {
		document.removeEventListener('mousemove', handleMouseMove);
		document.removeEventListener('mouseup', handleMouseUp);
	};

	const saveReposition = async () => {
		void coverPictureState.setIsRepositioningPicture(true);
		await onSaveReposition(curPos.current);
		setIsRepositionActive(false);
		setShouldShowReposition(false);
		void coverPictureState.setIsRepositioningPicture(false);
		removeListeners();
		await coverPictureState.setIsChangingPicture(false);
		await coverPictureState.setShouldShowReposition(false);
		forceUpdate?.(Math.random());
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'repositionSaveButtonClicked',
				source: analyticsSource,
				attributes: {
					isLivePage,
				},
			},
		}).fire();
	};

	const cancelReposition = async () => {
		const img = document.getElementById('actual-cover-picture');
		setIsRepositionActive(false);
		setShouldShowReposition(false);
		void coverPictureState.setIsRepositioningPicture(false);
		removeListeners();
		await coverPictureState.setShouldShowReposition(false);
		await coverPictureState.setIsChangingPicture(false);
		forceUpdate?.(Math.random());
		if (img) {
			img.style.objectPosition = `center ${initialPicturePosition}%`;
		}
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'repositionCancelButtonClicked',
				source: analyticsSource,
			},
		}).fire();
	};

	return (
		<RepositionImageWrapper
			onMouseDown={userOffline ? undefined : handleRepositionImageMouseDown}
			data-testid="reposition-div"
			userOffline={userOffline}
			dynamicCoverImageHeight={dynamicCoverImageHeight}
		>
			<RepositionWrapper userOffline={userOffline}>
				<DragToRepositionTextWrapper>
					{formatMessage(i18n.dragToReposition)}
				</DragToRepositionTextWrapper>
				<RepositionButtonWrapper>
					<Button
						testId="save-reposition-button"
						spacing="compact"
						onClick={saveReposition}
						style={{
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
							background: token('color.background.input'),
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
							marginRight: token('space.200'),
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
							marginLeft: token('space.negative.100'),
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
							marginBottom: token('space.025'),
						}}
						isDisabled={userOffline}
						autoFocus={!userOffline && expValEquals('a11y-fixes-week4-may-2026', 'isEnabled', true)}
					>
						<RepositionButtonText>{formatMessage(i18n.saveReposition)}</RepositionButtonText>
					</Button>
					<Button
						testId="cancel-reposition-button"
						spacing="compact"
						onClick={cancelReposition}
						style={{
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
							background: token('color.background.input'),
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
							marginBottom: token('space.025'),
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
							marginLeft: token('space.negative.050'),
						}}
						isDisabled={userOffline}
					>
						<RepositionButtonText>{formatMessage(i18n.cancelReposition)}</RepositionButtonText>
					</Button>
				</RepositionButtonWrapper>
			</RepositionWrapper>
		</RepositionImageWrapper>
	);
};
